import { useEffect, useRef, useCallback } from 'react';

const useWebSocket = (url, userId, onMessage) => {
  const socketRef = useRef(null);
  const pingInterval = useRef(null);

  const connectWebSocket = useCallback(() => {
    if (!url || !userId) return;

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return; // Do not connect if already connected
    }

    socketRef.current = new WebSocket(url);

    socketRef.current.onopen = () => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const language = navigator.language;

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ 
          type: 'registerUser', 
          userId, 
          userAgent, 
          platform, 
          language
        }));
      }

       // Make sure that there are no multiple ping intervals.
       if (!pingInterval.current) {
        pingInterval.current = setInterval(() => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            //console.log('🏓 Pinging the server');
            socketRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
      }
    };

    socketRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === 'pong') {
        //console.log('📡 Pong received from server');
      } else if (onMessage) {
        onMessage(message);
      }
    };

    socketRef.current.onclose = () => {
      //console.log('❌ WebSocket offline');
      clearInterval(pingInterval.current);
    };

    socketRef.current.onerror = (error) => {
      //console.error('⚠️ Error in WebSocket:', error.message);
      socketRef.current.close();
    };
  }, [url, userId, onMessage]);

  const reconnect = useCallback(() => {
    if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
      console.log('Reconnecting to WebSocket...');
      connectWebSocket();
    } else {
      //console.log('The WebSocket is connected or in the process of connecting.');
    }
  }, [connectWebSocket]);

  const sendMessage = (message) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      //console.warn('WebSocket is not connected. The message cannot be sent.');
    }
  };

  const checkConnection = () => {
    return socketRef.current?.readyState === WebSocket.OPEN;
  };

  useEffect(() => {
    connectWebSocket();
    return () => {
      //console.log('The connection will not close when disassembled.');
      clearInterval(pingInterval.current);
    };
  }, [connectWebSocket]);

  return { sendMessage, reconnect, checkConnection };
};

export default useWebSocket;